<script setup lang="ts">
/**
 * Affectation de matériel existant à un collaborateur (modale) — module Onboarding V2.
 *
 * Sert au « rattrapage du parc » : on déclare un ou plusieurs équipements déjà
 * détenus par le collaborateur (PC, téléphone, SIM, ligne, véhicule, badge…).
 * On compose une liste d'objets via un mini-formulaire (« draft ») avec champs
 * dynamiques selon le type choisi, puis on délègue au store
 * (`assignAssetsToCollaborator`).
 *
 * Pattern modale natif `<dialog class="modal">` DaisyUI — cf. OnbHandoverDialog.vue
 * et OnbReplacementDialog.vue. Le parent récupère un `ref` et appelle `open(userUuid)`
 * (exposé via defineExpose).
 */
import { computed, ref } from 'vue'
import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { useOnboardingStore } from '@/v2/stores/useOnboardingStore'
import {
  ASSET_CONDITIONS,
  ASSET_STATUS_META,
  ASSET_TYPES,
  ASSET_TYPE_BY_CODE,
  CATEGORY_META,
  CONDITION_LABEL,
  attributeLabel,
  isSensitiveAttribute,
} from '@/v2/onboarding/catalog'
import type { AssetCategory, AssetStatus, AssetTypeDef } from '@/v2/onboarding/types'

const emit = defineEmits<{
  assigned: [count: number]
}>()

const store = useOnboardingStore()

const dialog = ref<HTMLDialogElement | null>(null)

/** UUID du collaborateur ciblé (mémorisé à l'ouverture). */
const userUuid = ref('')

/** Équipement en cours de saisie. */
interface DraftAsset {
  typeCode: string
  attributes: Record<string, string>
  status: AssetStatus
  owner: string
  /** État du matériel à la fourniture (code, cf. catalog ASSET_CONDITIONS) — optionnel. */
  condition: string
}

/** Statut par défaut : rattrapage = déjà détenu → « Remis ». */
const DEFAULT_STATUS: AssetStatus = 'DELIVERED'
const DEFAULT_OWNER = 'Vous'

/** Statuts proposés (on exclut RETURNED et REPLACED, hors périmètre rattrapage). */
const STATUS_OPTIONS: AssetStatus[] = [
  'TO_ORDER',
  'ORDERED',
  'RECEIVED',
  'PREPARED',
  'DELIVERED',
]

function emptyDraft(owner: string = DEFAULT_OWNER): DraftAsset {
  return { typeCode: '', attributes: {}, status: DEFAULT_STATUS, owner, condition: '' }
}

const draft = ref<DraftAsset>(emptyDraft())
const items = ref<DraftAsset[]>([])

const titleId = 'onb-assign-asset-title'

/**
 * Types d'équipements groupés par catégorie pour les `<optgroup>`.
 * On ne retient que les catégories effectivement présentes dans le catalogue.
 */
const groupedTypes = computed<{ category: AssetCategory; label: string; types: AssetTypeDef[] }[]>(
  () => {
    const byCategory = new Map<AssetCategory, AssetTypeDef[]>()
    for (const t of ASSET_TYPES) {
      const bucket = byCategory.get(t.category) ?? []
      bucket.push(t)
      byCategory.set(t.category, bucket)
    }
    return (Object.keys(CATEGORY_META) as AssetCategory[])
      .filter((cat) => byCategory.has(cat))
      .map((cat) => ({
        category: cat,
        label: CATEGORY_META[cat].label,
        types: byCategory.get(cat) ?? [],
      }))
  },
)

/** Définition du type sélectionné dans le draft (clés d'attributs, label…). */
const draftTypeDef = computed<AssetTypeDef | null>(
  () => (draft.value.typeCode ? (ASSET_TYPE_BY_CODE[draft.value.typeCode] ?? null) : null),
)

/** Clés d'attributs dynamiques à afficher pour le type courant. */
const draftAttributeKeys = computed<string[]>(() => draftTypeDef.value?.attributeKeys ?? [])

/** Le draft courant est-il prêt à être ajouté / affecté ? (un type suffit) */
const draftValid = computed(() => !!draft.value.typeCode)

/** Au moins un équipement à affecter (liste OU draft valide). */
const canAssign = computed(() => items.value.length > 0 || draftValid.value)

/** Libellé d'un type d'équipement (avec repli sur le code). */
function typeLabel(typeCode: string): string {
  return ASSET_TYPE_BY_CODE[typeCode]?.label ?? typeCode
}

/** Identifiant principal affichable d'un équipement (N° de série ou IMEI). */
function primaryIdentifier(asset: DraftAsset): string {
  const serial = asset.attributes.serialNumber?.trim()
  const imei = asset.attributes.imei?.trim()
  return [serial, imei].filter(Boolean).join(' / ')
}

/** Copie défensive des attributs en retirant les valeurs vides. */
function cleanAttributes(attributes: Record<string, string>): Record<string, string> {
  const cleaned: Record<string, string> = {}
  for (const [key, value] of Object.entries(attributes)) {
    const trimmed = value?.trim()
    if (trimmed) cleaned[key] = trimmed
  }
  return cleaned
}

/** Réinitialise le draft en conservant le responsable saisi. */
function resetDraft(): void {
  draft.value = emptyDraft(draft.value.owner || DEFAULT_OWNER)
}

/** Réinitialise les attributs quand on change de type (clés différentes). */
function onTypeChange(): void {
  draft.value.attributes = {}
}

/** Ajoute le draft courant (s'il est valide) à la liste, puis réinitialise. */
function addToList(): void {
  if (!draftValid.value) return
  items.value.push({
    typeCode: draft.value.typeCode,
    attributes: { ...draft.value.attributes },
    status: draft.value.status,
    owner: draft.value.owner.trim() || DEFAULT_OWNER,
    condition: draft.value.condition,
  })
  resetDraft()
}

/** Retire un équipement de la liste. */
function removeItem(index: number): void {
  items.value.splice(index, 1)
}

/** Ouvre la modale pour un collaborateur (API publique consommée par le parent). */
function open(userUuidValue: string): void {
  userUuid.value = userUuidValue
  draft.value = emptyDraft()
  items.value = []
  dialog.value?.showModal()
}

function close(): void {
  dialog.value?.close()
}

defineExpose({ open })

/** Affecte tous les équipements (liste + draft courant éventuel) au collaborateur. */
async function assign(): Promise<void> {
  if (!canAssign.value) return

  const toAssign: DraftAsset[] = [...items.value]
  // Si l'utilisateur a saisi un draft sans cliquer « Ajouter », on l'inclut.
  if (draftValid.value) {
    toAssign.push({
      typeCode: draft.value.typeCode,
      attributes: { ...draft.value.attributes },
      status: draft.value.status,
      owner: draft.value.owner.trim() || DEFAULT_OWNER,
      condition: draft.value.condition,
    })
  }

  if (!toAssign.length) return

  const inputs = toAssign.map((a) => ({
    typeCode: a.typeCode,
    attributes: cleanAttributes(a.attributes),
    status: a.status,
    ownerName: a.owner.trim() || undefined,
    condition: a.condition || undefined,
  }))

  await store.assignAssetsToCollaborator(userUuid.value, inputs)
  emit('assigned', inputs.length)
  close()
}
</script>

<template>
  <dialog ref="dialog" class="modal" :aria-labelledby="titleId">
    <div class="modal-box w-full max-w-2xl max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
      <!-- En-tête + fermeture -->
      <div class="flex items-start justify-between gap-4">
        <div>
          <h2 :id="titleId" class="text-lg font-semibold">Affecter du matériel</h2>
          <p class="text-sm text-base-content/70">
            Déclarez le matériel déjà détenu (rattrapage du parc).
          </p>
        </div>
        <button
          type="button"
          class="btn btn-ghost btn-sm motion-reduce:transition-none"
          aria-label="Fermer"
          @click="close"
        >
          <XMarkIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <!-- Formulaire d'un équipement (draft) -->
      <section class="mt-4" aria-labelledby="onb-assign-form">
        <h3 id="onb-assign-form" class="text-base font-semibold">Équipement à ajouter</h3>
        <form class="mt-2 space-y-4" @submit.prevent="addToList">
          <!-- Type d'équipement -->
          <div>
            <label class="label" for="onb-assign-type">
              <span class="label-text">Type d'équipement</span>
            </label>
            <select
              id="onb-assign-type"
              v-model="draft.typeCode"
              class="select select-bordered w-full"
              @change="onTypeChange"
            >
              <option value="" disabled>Sélectionner un type</option>
              <optgroup v-for="group in groupedTypes" :key="group.category" :label="group.label">
                <option v-for="t in group.types" :key="t.code" :value="t.code">
                  {{ t.label }}
                </option>
              </optgroup>
            </select>
          </div>

          <!-- Champs dynamiques selon le type -->
          <div v-if="draftAttributeKeys.length" class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div v-for="key in draftAttributeKeys" :key="key">
              <label class="label" :for="`onb-assign-attr-${key}`">
                <span class="label-text">
                  {{ attributeLabel(key) }}
                  <span v-if="isSensitiveAttribute(key)" aria-hidden="true">🔒</span>
                </span>
              </label>
              <input
                :id="`onb-assign-attr-${key}`"
                v-model="draft.attributes[key]"
                :type="isSensitiveAttribute(key) ? 'password' : 'text'"
                class="input input-bordered w-full"
                :class="{ 'tabular-nums': key === 'serialNumber' || key === 'imei' }"
                :autocomplete="isSensitiveAttribute(key) ? 'new-password' : 'off'"
                :aria-describedby="
                  isSensitiveAttribute(key) ? `onb-assign-attr-${key}-hint` : undefined
                "
              />
              <p
                v-if="isSensitiveAttribute(key)"
                :id="`onb-assign-attr-${key}-hint`"
                class="mt-1 text-sm text-base-content/80"
              >
                Donnée sensible (coffre chiffré).
              </p>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <!-- Statut -->
            <div>
              <label class="label" for="onb-assign-status">
                <span class="label-text">Statut</span>
              </label>
              <select
                id="onb-assign-status"
                v-model="draft.status"
                class="select select-bordered w-full"
              >
                <option v-for="s in STATUS_OPTIONS" :key="s" :value="s">
                  {{ ASSET_STATUS_META[s].label }}
                </option>
              </select>
            </div>

            <!-- Responsable -->
            <div>
              <label class="label" for="onb-assign-owner">
                <span class="label-text">Responsable</span>
              </label>
              <input
                id="onb-assign-owner"
                v-model="draft.owner"
                type="text"
                class="input input-bordered w-full"
              />
            </div>

            <!-- État du matériel (optionnel) -->
            <div>
              <label class="label" for="onb-assign-condition">
                <span class="label-text">État</span>
              </label>
              <select
                id="onb-assign-condition"
                v-model="draft.condition"
                class="select select-bordered w-full"
              >
                <option value="">Non précisé</option>
                <option v-for="c in ASSET_CONDITIONS" :key="c.value" :value="c.value">
                  {{ c.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Ajouter à la liste -->
          <div class="flex justify-end">
            <button
              type="submit"
              class="btn btn-ghost motion-reduce:transition-none"
              :disabled="!draftValid"
            >
              <PlusIcon class="h-5 w-5" aria-hidden="true" />
              Ajouter à la liste
            </button>
          </div>
        </form>
      </section>

      <!-- Liste des équipements à affecter -->
      <section class="mt-6" aria-labelledby="onb-assign-list">
        <h3 id="onb-assign-list" class="text-base font-semibold">
          Équipements à affecter
          <span class="text-base-content/70">({{ items.length }})</span>
        </h3>
        <div class="mt-2 overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th scope="col">Type</th>
                <th scope="col">N° Série / IMEI</th>
                <th scope="col">Statut</th>
                <th scope="col">État</th>
                <th scope="col"><span class="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!items.length">
                <td colspan="5" class="text-center text-base-content/80">
                  Aucun équipement ajouté pour l'instant.
                </td>
              </tr>
              <tr v-for="(item, index) in items" :key="index">
                <td class="font-medium text-base-content">{{ typeLabel(item.typeCode) }}</td>
                <td class="tabular-nums text-base-content/80">
                  {{ primaryIdentifier(item) || '—' }}
                </td>
                <td>
                  <span :class="ASSET_STATUS_META[item.status].badgeClass">
                    {{ ASSET_STATUS_META[item.status].label }}
                  </span>
                </td>
                <td class="text-base-content/80">
                  {{ item.condition ? CONDITION_LABEL[item.condition] : '—' }}
                </td>
                <td class="text-right">
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm motion-reduce:transition-none"
                    :aria-label="`Retirer ${typeLabel(item.typeCode)}`"
                    @click="removeItem(index)"
                  >
                    <TrashIcon class="h-5 w-5" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Actions -->
      <div class="modal-action flex-wrap">
        <button type="button" class="btn btn-ghost motion-reduce:transition-none" @click="close">
          Annuler
        </button>
        <button
          type="button"
          class="btn btn-primary motion-reduce:transition-none"
          :disabled="!canAssign"
          @click="assign"
        >
          Affecter
        </button>
      </div>
    </div>

    <form method="dialog" class="modal-backdrop">
      <button aria-label="Fermer">Fermer</button>
    </form>
  </dialog>
</template>
